import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  HiPlus,
  HiPhotograph,
  HiPlay,
  HiCheck,
  HiChartBar,
  HiUpload,
  HiTrash,
} from 'react-icons/hi';
import apiClient from '../api/client';
import { useNotification } from '../context/NotificationContext';

/** Клиентские названия типов теста (внутри API остаются sequential / rotational). */
const TEST_TYPE_LABELS = {
  sequential: {
    short: 'До и после',
    option: 'До и после — сравнение периодов',
    description: 'Сравните статистику за выбранный период до смены фото и за период после.',
  },
  rotational: {
    short: 'Несколько вариантов',
    option: 'Несколько вариантов — смена по очереди',
    description:
      'Загрузите несколько изображений: система будет менять их автоматически через заданный интервал. '
      + 'Статистика считается отдельно за время показа каждого варианта.',
  },
};

function testTypeLabel(type, variant = 'short') {
  return TEST_TYPE_LABELS[type]?.[variant] || type;
}

const METRIC_OPTIONS = [
  { key: 'open_card_count', label: 'Просмотры карточки', tableLabel: ['Просмотры', 'карточки'] },
  { key: 'add_to_cart_count', label: 'В корзину', tableLabel: ['В корзину'] },
  { key: 'clicks', label: 'Клики', adOnly: true, tableLabel: ['Клики'] },
  { key: 'orders_count', label: 'Заказы', tableLabel: ['Заказы'] },
  { key: 'orders_sum_rub', label: 'Выручка, ₽', tableLabel: ['Выручка,', '₽'] },
  { key: 'ad_spend', label: 'Расход рекламы, ₽', adOnly: true, tableLabel: ['Расход', 'рекламы, ₽'] },
  { key: 'ctr', label: 'CTR, %', adOnly: true, showInAll: true, tableLabel: ['CTR, %'] },
  { key: 'open_to_cart_rate', label: 'Конв. карточка → корзина, %', tableLabel: ['Конв.', 'карт. → корз., %'] },
  { key: 'cart_to_order_rate', label: 'Конв. корзина → заказ, %', tableLabel: ['Конв.', 'корз. → заказ, %'] },
  { key: 'open_to_order_rate', label: 'Конв. карточка → заказ, %', tableLabel: ['Конв.', 'карт. → заказ, %'] },
  { key: 'avg_check', label: 'Средний чек, ₽', tableLabel: ['Средний', 'чек, ₽'] },
];

const ALL_METRIC_KEYS = METRIC_OPTIONS.map((m) => m.key);

const STATUS_LABELS = {
  draft: 'Черновик',
  running: 'Идёт',
  completed: 'Завершён',
  cancelled: 'Отменён',
};

const STATUS_BADGE_STYLES = {
  draft: 'bg-slate-100 text-slate-600 border border-slate-200',
  running: 'bg-amber-100 text-amber-800 border border-amber-200',
  completed: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-700 border border-rose-200',
};

function statusBadgeClass(status) {
  return STATUS_BADGE_STYLES[status] || STATUS_BADGE_STYLES.draft;
}

function formatDateInput(d) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

function defaultSequentialPeriods() {
  const pivot = new Date();
  const baselineTo = new Date(pivot);
  baselineTo.setDate(baselineTo.getDate() - 1);
  const baselineFrom = new Date(baselineTo);
  baselineFrom.setDate(baselineFrom.getDate() - 6);
  const testTo = new Date(pivot);
  testTo.setDate(testTo.getDate() + 6);
  return {
    baseline_from: formatDateInput(baselineFrom),
    baseline_to: formatDateInput(baselineTo),
    test_from: formatDateInput(pivot),
    test_to: formatDateInput(testTo),
  };
}

function formatMetric(value, key) {
  if (value == null || value === '') return '—';
  if (key === 'ctr' || key.includes('rate')) {
    return `${Number(value).toFixed(2)}%`;
  }
  return Math.round(Number(value)).toLocaleString('ru-RU');
}

function isAdOnlyMetric(key) {
  return METRIC_OPTIONS.find((m) => m.key === key)?.adOnly === true;
}

function isAdMetricVisibleInLayer(key, layer) {
  const m = METRIC_OPTIONS.find((x) => x.key === key);
  if (!m?.adOnly) return true;
  if (m.showInAll && layer === 'all') return true;
  return layer === 'ad';
}

function isMetricOptionVisible(m, layer, hasAd) {
  if (m.adOnly && !hasAd) return false;
  if (m.showInAll) return layer === 'all' || layer === 'ad';
  if (m.adOnly) return layer === 'ad';
  return true;
}

function formatCardLabel(card) {
  if (!card) return '';
  const vendor = card.vendor_code?.trim() || '—';
  const nm = card.nm_id ?? '—';
  const title = card.title?.trim() || 'Без названия';
  return `${vendor} · ${nm} · ${title}`;
}

const AD_FUNNEL_METRIC_MAP = {
  open_card_count: 'clicks',
  add_to_cart_count: 'atbs',
  orders_count: 'orders',
  orders_sum_rub: 'revenue',
};

function adLayerMetricValue(ad, key) {
  if (!ad) return null;
  if (key === 'ctr') {
    const views = Number(ad.views) || 0;
    const clicks = Number(ad.clicks) || 0;
    return views > 0 ? (clicks / views) * 100 : 0;
  }
  if (key === 'ad_spend') return ad.spend ?? null;
  if (['open_card_count', 'add_to_cart_count', 'orders_count', 'orders_sum_rub'].includes(key)
    && ad[key] != null) {
    return ad[key];
  }
  // Просмотры карточки с рекламы = клики (показы в воронке не передаются)
  if (key === 'clicks') {
    if (ad.open_card_count != null) return ad.open_card_count;
    return ad.clicks ?? 0;
  }
  const mapped = AD_FUNNEL_METRIC_MAP[key];
  if (mapped) return ad[mapped] ?? 0;
  return null;
}

function periodLayerMetricValue(totals, layer, key) {
  if (!totals) return null;
  if (!isAdMetricVisibleInLayer(key, layer)) return null;
  if (layer === 'all') {
    if (key === 'ctr') return adLayerMetricValue(totals.ad, key);
    return totals.cabinet?.[key] ?? null;
  }
  if (layer === 'ad') return adLayerMetricValue(totals.ad, key);
  return totals.organic?.[key] ?? null;
}

function layerValue(totalsRow, layer, key) {
  if (!totalsRow) return null;
  if (!isAdMetricVisibleInLayer(key, layer)) return null;
  if (layer === 'all') return totalsRow.cabinet;
  if (layer === 'ad') return totalsRow.ad;
  return totalsRow.organic;
}

function periodMetricValue(period, layer, key) {
  return periodLayerMetricValue(period, layer, key);
}

function layerDeltaPct(row, layer, key) {
  if (key === 'ctr' && (layer === 'all' || layer === 'ad')) return row.delta_ad_pct;
  if (isAdOnlyMetric(key)) {
    return layer === 'ad' ? row.delta_ad_pct : null;
  }
  if (layer === 'all') return row.delta_cabinet_pct;
  if (layer === 'ad') return row.delta_ad_pct;
  return row.delta_organic_pct;
}

function buildCumulativeFromDaily(dailyRows, metricKey, layer) {
  const layerKey = layer === 'ad' ? 'ad' : layer === 'organic' ? 'organic' : 'cabinet';
  const rows = dailyRows || [];

  if (metricKey === 'ctr' && layer !== 'organic') {
    let cumViews = 0;
    let cumClicks = 0;
    return rows.map((row) => {
      cumViews += Number(row.ad?.views) || 0;
      cumClicks += Number(row.ad?.clicks) || 0;
      const ctr = cumViews > 0 ? Number(((cumClicks / cumViews) * 100).toFixed(2)) : 0;
      return { date: row.date, value: ctr, cumulative: ctr };
    });
  }

  const rateKeys = new Set(['open_to_cart_rate', 'cart_to_order_rate', 'open_to_order_rate', 'avg_check']);
  if (rateKeys.has(metricKey) && layerKey !== 'ad') {
    let cumOpens = 0;
    let cumCart = 0;
    let cumOrders = 0;
    let cumRevenue = 0;
    return rows.map((row) => {
      const src = row[layerKey] || {};
      cumOpens += Number(src.open_card_count) || 0;
      cumCart += Number(src.add_to_cart_count) || 0;
      cumOrders += Number(src.orders_count) || 0;
      cumRevenue += Number(src.orders_sum_rub) || 0;
      let val = 0;
      if (metricKey === 'open_to_cart_rate') val = cumOpens > 0 ? (cumCart / cumOpens) * 100 : 0;
      else if (metricKey === 'cart_to_order_rate') val = cumCart > 0 ? (cumOrders / cumCart) * 100 : 0;
      else if (metricKey === 'open_to_order_rate') val = cumOpens > 0 ? (cumOrders / cumOpens) * 100 : 0;
      else if (metricKey === 'avg_check') val = cumOrders > 0 ? cumRevenue / cumOrders : 0;
      if (metricKey === 'avg_check') val = Math.round(val);
      else val = Number(val.toFixed(2));
      return { date: row.date, value: val, cumulative: val };
    });
  }

  let cumulative = 0;
  return rows.map((row) => {
    let val;
    if (layerKey === 'ad') {
      val = Number(adLayerMetricValue(row.ad, metricKey)) || 0;
    } else if (layer === 'all' && metricKey === 'ctr') {
      val = Number(adLayerMetricValue(row.ad, metricKey)) || 0;
    } else {
      val = Number(row[layerKey]?.[metricKey]) || 0;
    }
    cumulative += val;
    return { date: row.date, value: val, cumulative };
  });
}

function buildRotationalChartFromDaily(dailyRows, variants, metricKey, layer) {
  const dates = [...new Set((dailyRows || []).map((r) => r.date))].sort();
  const variantList = (variants?.length ? variants : [...new Map(
    (dailyRows || []).map((r) => [r.variant_id, { variant_id: r.variant_id, label: r.variant_label || `Вариант ${r.variant_id}` }]),
  ).values()]);
  const series = variantList.map((v) => ({
    variant_id: v.variant_id,
    label: v.label,
    points: dates.map((date) => {
      const row = (dailyRows || []).find((r) => r.date === date && r.variant_id === v.variant_id);
      return {
        date,
        value: row ? (periodLayerMetricValue(row, layer, metricKey) ?? 0) : 0,
      };
    }),
  }));
  return { type: 'rotational', dates, series, metric: metricKey };
}

function buildChartSeries(results, metricKey, layer) {
  if (!results) return null;
  if (results.test_type === 'rotational') {
    return buildRotationalChartFromDaily(
      results.stats_table?.daily || [],
      results.variants || [],
      metricKey,
      layer,
    );
  }
  const baselineDaily = results.baseline?.daily || [];
  const testDaily = results.test_period?.daily || [];
  if (!baselineDaily.length && !testDaily.length) return null;
  return {
    type: 'sequential',
    control: buildCumulativeFromDaily(baselineDaily, metricKey, layer),
    variation: buildCumulativeFromDaily(testDaily, metricKey, layer),
  };
}

function AbComparisonChart({ chart, seriesData, metricKey, metricLabel, layer, pivotLabel, testType }) {
  if (!chart && !seriesData) return null;

  const rotationalChart = seriesData?.type === 'rotational' ? seriesData : chart;
  if (rotationalChart?.type === 'rotational' && rotationalChart.series?.length) {
    return (
      <AbRotationalChart
        chart={rotationalChart}
        metricKey={metricKey}
        metricLabel={metricLabel}
        layer={layer}
      />
    );
  }

  if (isAdOnlyMetric(metricKey) && !isAdMetricVisibleInLayer(metricKey, layer)) {
    return (
      <div className="wb-card p-6 text-center text-[13px] text-[var(--wb-text-muted)]">
        Метрика «{metricLabel}» доступна только для слоя «Реклама».
      </div>
    );
  }
  const controlKey = layer === 'all' ? 'control_cabinet' : layer === 'ad' ? 'control_ad' : 'control';
  const variationKey = layer === 'all' ? 'variation_cabinet' : layer === 'ad' ? 'variation_ad' : 'variation';
  const control = seriesData?.control || chart?.[controlKey] || chart?.control || [];
  const variation = seriesData?.variation || chart?.[variationKey] || chart?.variation || [];

  const merged = [];
  const maxLen = Math.max(control.length, variation.length);
  for (let i = 0; i < maxLen; i++) {
    merged.push({
      label: control[i]?.date || variation[i]?.date || control[i]?.index || variation[i]?.index || i + 1,
      control: control[i]?.cumulative ?? 0,
      variation: variation[i]?.cumulative ?? 0,
    });
  }

  if (!merged.length) {
    return (
      <div className="wb-card p-6 text-center text-[13px] text-[var(--wb-text-muted)]">
        Недостаточно данных для графика. Дождитесь накопления статистики за выбранные периоды.
      </div>
    );
  }

  const pivotIndex = control.length > 0 ? control.length - 0.5 : null;

  return (
    <div className="wb-card p-4 overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[15px]">До и после — {metricLabel}</h3>
        <div className="flex gap-4 text-[13px]">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-[#f97316]" />
            До смены
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-[#059669]" />
            После смены
          </span>
        </div>
      </div>
      <div
        className="rounded-xl p-3"
        style={{
          background:
            'linear-gradient(135deg, rgba(237,233,254,0.9) 0%, rgba(236,253,245,0.85) 50%, rgba(255,251,235,0.9) 100%)',
        }}
      >
        <ResponsiveContainer width="100%" height={360}>
          <LineChart key={`${metricKey}-${layer}`} data={merged} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(124,58,237,0.12)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#5c5470' }}
              tickLine={false}
              axisLine={{ stroke: '#e6e2ef' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#5c5470' }}
              tickLine={false}
              axisLine={{ stroke: '#e6e2ef' }}
              width={48}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 10,
                border: '1px solid #e6e2ef',
                fontSize: 12,
              }}
              formatter={(v) => formatMetric(v, metricKey)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {pivotIndex != null && chart.control?.length > 0 && (
              <ReferenceLine
                x={merged[Math.min(control.length - 1, merged.length - 1)]?.label}
                stroke="#7c3aed"
                strokeDasharray="4 4"
                label={{ value: pivotLabel || 'Смена фото', fill: '#7c3aed', fontSize: 11 }}
              />
            )}
            <Line
              type="monotone"
              dataKey="control"
              name="До смены"
              stroke="#f97316"
              strokeWidth={2.5}
              dot={{ r: 3, fill: '#f97316' }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="variation"
              name="После смены"
              stroke="#059669"
              strokeWidth={2.5}
              dot={{ r: 3, fill: '#059669' }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const VARIANT_CHART_COLORS = ['#f97316', '#059669', '#7c3aed', '#2563eb', '#dc2626'];

function AbRotationalChart({ chart, metricKey, metricLabel, layer }) {
  if (isAdOnlyMetric(metricKey) && !isAdMetricVisibleInLayer(metricKey, layer)) {
    return (
      <div className="wb-card p-6 text-center text-[13px] text-[var(--wb-text-muted)]">
        Метрика «{metricLabel}» доступна только для слоя «Реклама».
      </div>
    );
  }

  const series = chart.series || [];
  const dates = chart.dates || [];
  const merged = dates.map((date) => {
    const row = { date };
    series.forEach((s) => {
      const pt = s.points.find((p) => p.date === date);
      row[`v${s.variant_id}`] = pt?.value ?? 0;
    });
    return row;
  });

  if (!merged.length) {
    return (
      <div className="wb-card p-6 text-center text-[13px] text-[var(--wb-text-muted)]">
        Недостаточно данных для графика. Дождитесь накопления почасовой статистики.
      </div>
    );
  }

  return (
    <div className="wb-card p-4 overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[15px]">Динамика по дням — {metricLabel}</h3>
      </div>
      <div
        className="rounded-xl p-3"
        style={{
          background:
            'linear-gradient(135deg, rgba(237,233,254,0.9) 0%, rgba(236,253,245,0.85) 50%, rgba(255,251,235,0.9) 100%)',
        }}
      >
        <ResponsiveContainer width="100%" height={360}>
          <LineChart key={`${metricKey}-${layer}`} data={merged} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(124,58,237,0.12)" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#5c5470' }}
              tickLine={false}
              axisLine={{ stroke: '#e6e2ef' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#5c5470' }}
              tickLine={false}
              axisLine={{ stroke: '#e6e2ef' }}
              width={48}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 10,
                border: '1px solid #e6e2ef',
                fontSize: 12,
              }}
              formatter={(v) => formatMetric(v, metricKey)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {series.map((s, i) => (
              <Line
                key={s.variant_id}
                type="monotone"
                dataKey={`v${s.variant_id}`}
                name={s.label}
                stroke={VARIANT_CHART_COLORS[i % VARIANT_CHART_COLORS.length]}
                strokeWidth={2.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const TABLE_METRICS = METRIC_OPTIONS;

function emptyFunnelAcc() {
  return { open_card_count: 0, add_to_cart_count: 0, orders_count: 0, orders_sum_rub: 0 };
}

function emptyAdAcc() {
  return {
    views: 0,
    clicks: 0,
    atbs: 0,
    orders: 0,
    revenue: 0,
    spend: 0,
    open_card_count: 0,
    add_to_cart_count: 0,
    orders_count: 0,
    orders_sum_rub: 0,
  };
}

function addFunnelAcc(acc, src = {}) {
  acc.open_card_count += Number(src.open_card_count) || 0;
  acc.add_to_cart_count += Number(src.add_to_cart_count) || 0;
  acc.orders_count += Number(src.orders_count) || 0;
  acc.orders_sum_rub += Number(src.orders_sum_rub) || 0;
}

function deriveFunnelAcc(acc) {
  const open = acc.open_card_count;
  const cart = acc.add_to_cart_count;
  const orders = acc.orders_count;
  const revenue = Math.round(acc.orders_sum_rub);
  return {
    open_card_count: Math.round(open),
    add_to_cart_count: Math.round(cart),
    orders_count: Math.round(orders),
    orders_sum_rub: revenue,
    open_to_cart_rate: open > 0 ? Number(((cart / open) * 100).toFixed(2)) : 0,
    cart_to_order_rate: cart > 0 ? Number(((orders / cart) * 100).toFixed(2)) : 0,
    open_to_order_rate: open > 0 ? Number(((orders / open) * 100).toFixed(2)) : 0,
    avg_check: orders > 0 ? Math.round(revenue / orders) : null,
  };
}

function buildPeriodTotalRows(rows) {
  const byVariant = new Map();
  for (const row of rows || []) {
    const id = row.variant_id;
    if (id == null) continue;
    if (!byVariant.has(id)) {
      byVariant.set(id, {
        variant_id: id,
        variant_label: row.variant_label || `Вариант ${id}`,
        cabinet: emptyFunnelAcc(),
        organic: emptyFunnelAcc(),
        ad: emptyAdAcc(),
      });
    }
    const t = byVariant.get(id);
    addFunnelAcc(t.cabinet, row.cabinet);
    addFunnelAcc(t.organic, row.organic);
    const ad = row.ad || {};
    t.ad.views += Number(ad.views) || 0;
    t.ad.clicks += Number(ad.clicks) || 0;
    t.ad.atbs += Number(ad.atbs) || 0;
    t.ad.orders += Number(ad.orders) || 0;
    t.ad.revenue += Number(ad.revenue) || 0;
    t.ad.spend += Number(ad.spend) || 0;
    t.ad.open_card_count += Number(ad.open_card_count ?? ad.clicks) || 0;
    t.ad.add_to_cart_count += Number(ad.add_to_cart_count ?? ad.atbs) || 0;
    t.ad.orders_count += Number(ad.orders_count ?? ad.orders) || 0;
    t.ad.orders_sum_rub += Number(ad.orders_sum_rub ?? ad.revenue) || 0;
  }

  return [...byVariant.values()]
    .sort((a, b) => {
      if (a.variant_id === 'original') return -1;
      if (b.variant_id === 'original') return 1;
      return String(a.variant_label).localeCompare(String(b.variant_label), 'ru');
    })
    .map((t) => {
      const cabinet = deriveFunnelAcc(t.cabinet);
      const organic = deriveFunnelAcc(t.organic);
      const adFunnel = deriveFunnelAcc({
        open_card_count: t.ad.open_card_count,
        add_to_cart_count: t.ad.add_to_cart_count,
        orders_count: t.ad.orders_count,
        orders_sum_rub: t.ad.orders_sum_rub,
      });
      const views = Math.round(t.ad.views);
      const clicks = Math.round(t.ad.clicks);
      return {
        date: 'Итого',
        is_total: true,
        variant_id: t.variant_id,
        variant_label: t.variant_label,
        cabinet,
        organic,
        ad: {
          views,
          clicks,
          atbs: Math.round(t.ad.atbs),
          orders: Math.round(t.ad.orders),
          revenue: Math.round(t.ad.revenue),
          spend: Math.round(t.ad.spend),
          ctr: views > 0 ? Number(((clicks / views) * 100).toFixed(2)) : 0,
          ...adFunnel,
        },
      };
    });
}

/** Относительная подсветка значения в колонке: красный → жёлтый → зелёный. */
function metricHeatStyle(value, min, max, higherIsBetter = true) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (max <= min) {
    return { backgroundColor: 'hsl(140 45% 92%)', color: 'hsl(140 40% 28%)' };
  }
  let t = (Number(value) - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (!higherIsBetter) t = 1 - t;
  // 0 → красный (~8°), 0.5 → янтарный (~42°), 1 → зелёный (~142°)
  const hue = Math.round(8 + t * 134);
  const sat = Math.round(55 + t * 15);
  return {
    backgroundColor: `hsl(${hue} ${sat}% 92%)`,
    color: `hsl(${hue} ${Math.round(sat * 0.75)}% 28%)`,
  };
}

function buildColumnRanges(rows, metrics, metricLayer) {
  const ranges = {};
  for (const m of metrics) {
    const values = [];
    for (const row of rows) {
      const v = periodMetricValue(row, metricLayer, m.key);
      if (v != null && Number.isFinite(Number(v))) values.push(Number(v));
    }
    if (!values.length) {
      ranges[m.key] = null;
      continue;
    }
    ranges[m.key] = {
      min: Math.min(...values),
      max: Math.max(...values),
      higherIsBetter: m.key !== 'ad_spend',
    };
  }
  return ranges;
}

function HeatMetricCell({ value, metricKey, range }) {
  const text = formatMetric(value, metricKey);
  const style = range
    ? metricHeatStyle(value, range.min, range.max, range.higherIsBetter)
    : null;
  if (!style || text === '—') {
    return <span className="tabular-nums text-[13px]">{text}</span>;
  }
  return (
    <span
      className="inline-block min-w-[3rem] text-center px-2 py-0.5 rounded-md text-[13px] tabular-nums leading-tight"
      style={style}
    >
      {text}
    </span>
  );
}

function AbStatsTable({ statsTable, metricLayer, granularity, onGranularityChange, hasAd, className = '' }) {
  const dailyRows = statsTable?.daily || [];
  const hourlyRows = statsTable?.hourly || [];
  const totalRows = useMemo(
    () => buildPeriodTotalRows(
      (statsTable?.daily?.length ? statsTable.daily : statsTable?.hourly) || [],
    ),
    [statsTable],
  );

  const rows = granularity === 'total'
    ? totalRows
    : granularity === 'hour'
      ? hourlyRows
      : dailyRows;

  const visibleMetrics = TABLE_METRICS.filter((m) => isMetricOptionVisible(m, metricLayer, hasAd));
  const columnRanges = useMemo(
    () => buildColumnRanges(rows, visibleMetrics, metricLayer),
    [rows, visibleMetrics, metricLayer],
  );
  const showHourCol = granularity === 'hour';
  const showDateCol = granularity !== 'total';
  const empty = granularity === 'total'
    ? !totalRows.length
    : !rows.length;

  return (
    <div className={`wb-card overflow-hidden flex flex-col min-w-0 h-full ${className}`}>
      <div className="px-4 py-3 border-b border-[var(--wb-border)] flex items-center justify-between gap-2 shrink-0">
        <h4 className="font-semibold text-[15px]">Сводная таблица</h4>
        <div className="flex rounded-lg border border-[var(--wb-border)] overflow-hidden text-[13px] shrink-0">
          {[
            { id: 'total', label: 'Итого' },
            { id: 'day', label: 'По дням' },
            { id: 'hour', label: 'По часам' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onGranularityChange(opt.id)}
              className={`px-3 py-1.5 ${granularity === opt.id ? 'bg-[var(--wb-primary)] text-white' : 'bg-white text-[var(--wb-text-secondary)]'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-auto flex-1 min-h-0">
        {empty ? (
          <p className="p-4 text-[13px] text-[var(--wb-text-muted)] text-center">
            Нет данных для таблицы. Статистика появится после почасовой синхронизации.
          </p>
        ) : (
          <table className="w-full text-[13px] table-fixed">
            <thead className="bg-[var(--wb-surface-muted)] sticky top-0 z-10">
              <tr>
                {showDateCol && (
                  <th className="text-left px-2 py-2.5 font-medium w-[5.5rem]">Дата</th>
                )}
                {showHourCol && (
                  <th className="text-left px-2 py-2.5 font-medium w-12">Час</th>
                )}
                <th className="text-left px-2 py-2.5 font-medium w-[5.5rem]">Вариант</th>
                {visibleMetrics.map((m) => (
                  <th
                    key={m.key}
                    className="text-center px-1.5 py-2.5 font-medium align-bottom leading-snug"
                    title={m.label}
                  >
                    <span className="inline-block text-[13px] font-medium text-[var(--wb-text-secondary)]">
                      {(m.tableLabel || [m.label]).map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={`${row.date}-${row.hour ?? ''}-${row.variant_id}-${i}`}
                  className={`border-t border-[var(--wb-border)] hover:bg-[var(--wb-surface-muted)]/40 ${granularity === 'total' ? 'font-semibold' : ''}`}
                >
                  {showDateCol && (
                    <td className="px-2 py-2.5 whitespace-nowrap">{row.date}</td>
                  )}
                  {showHourCol && (
                    <td className="px-2 py-2.5 tabular-nums whitespace-nowrap">
                      {String(row.hour).padStart(2, '0')}:00
                    </td>
                  )}
                  <td className="px-2 py-2.5 max-w-[5.5rem] truncate" title={row.variant_label}>
                    {row.variant_label}
                  </td>
                  {visibleMetrics.map((m) => (
                    <td key={m.key} className="px-1 py-2.5 text-center whitespace-nowrap">
                      <HeatMetricCell
                        value={periodMetricValue(row, metricLayer, m.key)}
                        metricKey={m.key}
                        range={columnRanges[m.key]}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function TrafficLayerToggle({ layer, onChange, hasAd }) {
  return (
    <div className="flex flex-wrap gap-2">
      {[
        { id: 'all', label: 'Все' },
        { id: 'organic', label: 'Органика' },
        ...(hasAd ? [{ id: 'ad', label: 'Реклама' }] : []),
      ].map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-3 py-1.5 rounded-lg text-[13px] font-medium border transition-colors ${
            layer === opt.id
              ? 'bg-[var(--wb-primary)] text-white border-[var(--wb-primary)]'
              : 'bg-white border-[var(--wb-border)] text-[var(--wb-text-secondary)] hover:border-[var(--wb-primary)]'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function AbVariantImage({ testId, variantId, clientId, alt, className }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!testId || !variantId || !clientId) return undefined;
    let objectUrl;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await apiClient.get(
          `/creative-ab/tests/${testId}/variants/${variantId}/image`,
          { params: { client_id: clientId }, responseType: 'blob' },
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setSrc(objectUrl);
        setFailed(false);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [testId, variantId, clientId]);

  if (failed) {
    return (
      <div className={`flex items-center justify-center bg-[var(--wb-bg-muted)] text-[var(--wb-text-muted)] min-h-[5rem] w-full rounded-lg ${className}`}>
        <HiPhotograph className="w-6 h-6" />
      </div>
    );
  }
  if (!src) {
    return <div className={`animate-pulse bg-[var(--wb-bg-muted)] min-h-[5rem] w-full rounded-lg ${className}`} />;
  }
  return <img src={src} alt={alt || ''} className={className} />;
}

function AbOriginalImage({ testId, clientId, alt, className }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!testId || !clientId) return undefined;
    let objectUrl;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await apiClient.get(
          `/creative-ab/tests/${testId}/original-image`,
          { params: { client_id: clientId }, responseType: 'blob' },
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setSrc(objectUrl);
        setFailed(false);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [testId, clientId]);

  if (failed) {
    return (
      <div className={`flex items-center justify-center bg-[var(--wb-bg-muted)] text-[var(--wb-text-muted)] min-h-[5rem] w-full rounded-lg ${className}`}>
        <HiPhotograph className="w-6 h-6" />
      </div>
    );
  }
  if (!src) {
    return <div className={`animate-pulse bg-[var(--wb-bg-muted)] min-h-[5rem] w-full rounded-lg ${className}`} />;
  }
  return <img src={src} alt={alt || ''} className={className} />;
}

function VariantSummaryCard({
  row,
  testId,
  clientId,
  metricLayer,
  hasAd,
  isOriginal,
  isWinner,
  canApply,
  applying,
  onApply,
}) {
  const summary = row.summary;
  const period = row.period || summary;
  const from = period.from || summary?.from;
  const to = period.to || summary?.to;

  return (
    <div className={`flex gap-4 items-start border-b border-[var(--wb-border)] pb-4 last:border-0 ${isOriginal ? 'bg-[var(--wb-surface-muted)]/30 -mx-2 px-2 pt-2 rounded-lg border-dashed' : ''} ${isWinner ? 'ring-1 ring-amber-300/60 rounded-lg -mx-1 px-1 pt-1' : ''}`}>
      <div className="shrink-0 w-32 flex items-start justify-center self-start">
        {isOriginal ? (
          <AbOriginalImage
            testId={testId}
            clientId={clientId}
            alt={row.label}
            className="max-w-full max-h-44 w-auto h-auto object-contain rounded-lg border border-[var(--wb-border)] shadow-sm bg-white"
          />
        ) : (
          <AbVariantImage
            testId={testId}
            variantId={row.variant_id}
            clientId={clientId}
            alt={row.label}
            className="max-w-full max-h-44 w-auto h-auto object-contain rounded-lg border border-[var(--wb-border)] shadow-sm bg-white"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <p className="font-medium text-[13px]">
            {row.label}
            {isOriginal && (
              <span className="ml-1 text-[13px] font-normal text-[var(--wb-text-muted)]">(до теста)</span>
            )}
          </p>
          {isWinner && (
            <span className="inline-flex items-center text-[13px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
              ★ Победитель
            </span>
          )}
        </div>
        {from && (
          <p className="text-[13px] text-[var(--wb-text-muted)] mb-2">
            {from} — {to}
            {isOriginal && period.days
              ? ` · сопоставимый период (${period.days} дн.)`
              : ''}
            {!isOriginal && summary?.rotation_count > 1 ? ` · ${summary.rotation_count} ротации` : ''}
          </p>
        )}
        {!isOriginal && row.pending_rotation && (
          <p className="text-[13px] text-[var(--wb-text-muted)] mb-2 italic">
            Ещё не показывался в ротации — статистика появится после первого показа
          </p>
        )}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]">
          {METRIC_OPTIONS.filter((m) => isMetricOptionVisible(m, metricLayer, hasAd)).map((m) => (
            <div key={m.key} className="flex justify-between gap-2">
              <span className="text-[var(--wb-text-muted)] truncate">{m.label}</span>
              <span className="tabular-nums font-medium shrink-0">
                {formatMetric(periodMetricValue(summary, metricLayer, m.key), m.key)}
              </span>
            </div>
          ))}
        </div>
        {canApply && onApply && (
          <button
            type="button"
            className="wb-btn text-[13px] mt-3 py-1.5 px-3"
            disabled={applying}
            onClick={onApply}
          >
            {applying ? 'Установка…' : 'Установить на карточку'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function CreativeAbTests({ clientId, marketplace = 'wb' }) {
  const { confirm, notify } = useNotification();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [testDetail, setTestDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [cardSearch, setCardSearch] = useState('');
  const [cards, setCards] = useState([]);
  const [cardsTotal, setCardsTotal] = useState(0);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardDropdownOpen, setCardDropdownOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const cardPickerRef = useRef(null);
  const cardSearchDebounceRef = useRef(null);
  const prevShowCreateRef = useRef(false);
  const createInFlightRef = useRef(false);
  const [metricLayer, setMetricLayer] = useState('all');
  const [chartMetric, setChartMetric] = useState('open_to_order_rate');
  const [tableGranularity, setTableGranularity] = useState('total');
  const [applyingVariantId, setApplyingVariantId] = useState(null);
  const [applySuccess, setApplySuccess] = useState('');

  const periods = useMemo(() => defaultSequentialPeriods(), []);

  const [form, setForm] = useState({
    test_type: 'sequential',
    nm_id: '',
    photo_slot: 1,
    rotation_period_days: 7,
    ...periods,
    notes: '',
  });
  const [uploadFiles, setUploadFiles] = useState([]);

  const loadTests = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get('/creative-ab/tests', {
        params: { client_id: clientId, marketplace },
      });
      setTests(data.tests || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось загрузить тесты');
    } finally {
      setLoading(false);
    }
  }, [clientId, marketplace]);

  const loadTestDetail = useCallback(async (testId) => {
    if (!testId || !clientId) return;
    setDetailLoading(true);
    try {
      const { data } = await apiClient.get(`/creative-ab/tests/${testId}`, {
        params: { client_id: clientId },
      });
      setTestDetail(data);
      if (data?.results?.chart?.metric) {
        setChartMetric(data.results.chart.metric);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось загрузить результаты');
    } finally {
      setDetailLoading(false);
    }
  }, [clientId]);

  const searchCards = useCallback(async (q) => {
    if (!clientId) return;
    setCardsLoading(true);
    try {
      const { data } = await apiClient.get('/creative-ab/cards', {
        params: { client_id: clientId, q: q || undefined, marketplace },
      });
      setCards(data.cards || []);
      setCardsTotal(data.total ?? (data.cards || []).length);
    } catch (e) {
      setCards([]);
      setCardsTotal(0);
      setError(e.response?.data?.message || 'Не удалось загрузить карточки');
    } finally {
      setCardsLoading(false);
    }
  }, [clientId, marketplace]);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  useEffect(() => {
    if (showCreate && !prevShowCreateRef.current) {
      setCardSearch('');
      setSelectedCard(null);
      setCardDropdownOpen(true);
      setForm((f) => ({ ...f, nm_id: '' }));
      searchCards('');
    }
    prevShowCreateRef.current = showCreate;
  }, [showCreate, searchCards]);

  useEffect(() => {
    if (!showCreate || !clientId) return undefined;
    if (cardSearchDebounceRef.current) clearTimeout(cardSearchDebounceRef.current);
    cardSearchDebounceRef.current = setTimeout(() => {
      searchCards(cardSearch.trim());
    }, 250);
    return () => clearTimeout(cardSearchDebounceRef.current);
  }, [cardSearch, showCreate, clientId, searchCards]);

  useEffect(() => {
    if (!showCreate) return undefined;
    const onDocClick = (e) => {
      if (cardPickerRef.current && !cardPickerRef.current.contains(e.target)) {
        setCardDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showCreate]);

  useEffect(() => {
    if (selectedTestId) loadTestDetail(selectedTestId);
    else setTestDetail(null);
  }, [selectedTestId, loadTestDetail]);

  const handleCardInputChange = (value) => {
    setCardSearch(value);
    setCardDropdownOpen(true);
    if (selectedCard && formatCardLabel(selectedCard) !== value) {
      setSelectedCard(null);
      setForm((f) => ({ ...f, nm_id: '' }));
    }
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
    setForm((f) => ({ ...f, nm_id: String(card.nm_id) }));
    setCardSearch(formatCardLabel(card));
    setCardDropdownOpen(false);
  };

  const handleCreate = async () => {
    if (!form.nm_id) {
      setError('Выберите карточку');
      notify('Выберите карточку', 'error');
      return;
    }
    if (createInFlightRef.current || creating) return;
    if (form.test_type === 'rotational' && uploadFiles.length < 2) {
      setError('Для теста с несколькими вариантами загрузите минимум 2 изображения');
      notify('Загрузите минимум 2 изображения', 'error');
      return;
    }

    createInFlightRef.current = true;
    setCreating(true);
    setError('');
    setSuccessMessage('');
    try {
      const { data } = await apiClient.post('/creative-ab/tests', {
        client_id: clientId,
        ...form,
        metrics: ALL_METRIC_KEYS,
        nm_id: Number(form.nm_id),
      });
      const testId = data.test?.id;
      let started = false;
      let startingInBackground = false;
      if (uploadFiles.length && testId) {
        const fd = new FormData();
        fd.append('client_id', clientId);
        fd.append('photo_slot', String(form.photo_slot));
        fd.append('test_type', form.test_type);
        uploadFiles.forEach((f) => fd.append('images', f));
        await apiClient.post(`/creative-ab/tests/${testId}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (form.test_type === 'rotational') {
          const startRes = await apiClient.post(`/creative-ab/tests/${testId}/start`, {
            client_id: clientId,
          });
          started = true;
          startingInBackground = Boolean(startRes?.data?.test?.starting_in_background);
        }
      }
      setShowCreate(false);
      setUploadFiles([]);
      await loadTests();
      if (testId) setSelectedTestId(testId);
      const msg = started
        ? (startingInBackground
          ? 'Тест создан и запущен. Загрузка фото на WB идёт в фоне — обычно до минуты.'
          : 'Тест создан и запущен')
        : 'Тест создан';
      setSuccessMessage(msg);
      notify(msg, 'success');
    } catch (e) {
      const msg = e.response?.data?.message || 'Не удалось создать тест';
      setError(msg);
      notify(msg, 'error');
    } finally {
      createInFlightRef.current = false;
      setCreating(false);
    }
  };

  const handleStart = async (testId) => {
    if (startingId) return;
    setStartingId(testId);
    setError('');
    setSuccessMessage('');
    try {
      const { data } = await apiClient.post(`/creative-ab/tests/${testId}/start`, {
        client_id: clientId,
      });
      await loadTests();
      await loadTestDetail(testId);
      const msg = data?.test?.starting_in_background
        ? 'Тест запущен. Загрузка фото на WB идёт в фоне.'
        : 'Тест запущен';
      setSuccessMessage(msg);
      notify(msg, 'success');
    } catch (e) {
      const msg = e.response?.data?.message || 'Не удалось запустить тест';
      setError(msg);
      notify(msg, 'error');
      await loadTests();
      if (selectedTestId === testId) await loadTestDetail(testId);
    } finally {
      setStartingId(null);
    }
  };

  const handleComplete = async (testId) => {
    if (completingId) return;
    const ok = await confirm({
      title: 'Завершить тест?',
      message: 'Ротация остановится. После завершения можно будет выбрать победителя и установить креатив на карточку.',
      confirmText: 'Завершить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    setCompletingId(testId);
    setError('');
    try {
      await apiClient.post(`/creative-ab/tests/${testId}/complete`, { client_id: clientId });
      await loadTests();
      await loadTestDetail(testId);
      setSuccessMessage('Тест завершён — можно выбрать победителя');
      notify('Тест завершён', 'success');
    } catch (e) {
      const msg = e.response?.data?.message || 'Ошибка';
      setError(msg);
      notify(msg, 'error');
    } finally {
      setCompletingId(null);
    }
  };

  const handleDelete = async (testId, e) => {
    e?.stopPropagation?.();
    if (deletingId) return;
    const ok = await confirm({
      title: 'Удалить тест?',
      message: 'Тест и загруженные изображения будут удалены без возможности восстановления.',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    setDeletingId(testId);
    setError('');
    try {
      await apiClient.delete(`/creative-ab/tests/${testId}`, {
        params: { client_id: clientId },
      });
      if (selectedTestId === testId) {
        setSelectedTestId(null);
        setTestDetail(null);
      }
      await loadTests();
      notify('Тест удалён', 'success');
      setSuccessMessage('Тест удалён');
    } catch (err) {
      const msg = err.response?.data?.message || 'Не удалось удалить тест';
      setError(msg);
      notify(msg, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleApplyVariant = async (variantId, label, { isOriginal = false } = {}) => {
    const slot = test?.photo_slot || 1;
    const ok = await confirm({
      title: 'Установить креатив на карточку',
      message: `Установить креатив «${label}» на карточку товара (слот ${slot})?`,
      confirmText: 'Установить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    const applyKey = isOriginal ? 'original' : variantId;
    setApplyingVariantId(applyKey);
    setApplySuccess('');
    setError('');
    try {
      const testId = test?.id || selectedTestId;
      const url = isOriginal
        ? `/creative-ab/tests/${testId}/original/apply`
        : `/creative-ab/tests/${testId}/variants/${variantId}/apply`;
      await apiClient.post(url, { client_id: clientId });
      const msg = `Креатив «${label}» установлен на карточку (слот ${slot})`;
      setApplySuccess(msg);
      notify(msg, 'success');
    } catch (e) {
      const msg = e.response?.data?.message || 'Не удалось установить креатив';
      setError(msg);
      notify(msg, 'error');
    } finally {
      setApplyingVariantId(null);
    }
  };

  const results = testDetail?.results;
  const test = testDetail?.test;
  const hasAd = results?.has_ad_data;

  const metricLabel = METRIC_OPTIONS.find((m) => m.key === chartMetric)?.label || chartMetric;

  const chartMetricOptions = useMemo(() => {
    return METRIC_OPTIONS.filter((m) => isMetricOptionVisible(m, metricLayer, hasAd));
  }, [hasAd, metricLayer]);

  const chartSeries = useMemo(
    () => buildChartSeries(results, chartMetric, metricLayer),
    [results, chartMetric, metricLayer],
  );

  useEffect(() => {
    if (!chartMetricOptions.some((m) => m.key === chartMetric) && chartMetricOptions.length) {
      setChartMetric(chartMetricOptions[0].key);
    }
  }, [chartMetricOptions, chartMetric]);

  return (
    <div className="flex flex-col w-full min-w-0 min-h-[calc(100dvh-7.5rem)]">
      {error && (
        <div className="wb-card p-3 mb-4 text-[13px] text-[var(--wb-danger)] border-[var(--wb-danger)] bg-[var(--wb-danger-bg)] shrink-0">
          {error}
        </div>
      )}
      {(successMessage || applySuccess) && (
        <div className="wb-card p-3 mb-4 text-[13px] text-[var(--wb-success)] border-[var(--wb-success)] bg-green-50 shrink-0">
          {applySuccess || successMessage}
        </div>
      )}

      {showCreate && (
        <div className="wb-card p-5 space-y-4 mb-4 shrink-0">
          <h2 className="font-semibold flex items-center gap-2">
            <HiPhotograph className="w-5 h-5 text-[var(--wb-primary)]" />
            Создать А/Б тест
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] text-[var(--wb-text-muted)] mb-1">Тип теста</label>
              <select
                className="wb-input w-full"
                value={form.test_type}
                onChange={(e) => setForm({ ...form, test_type: e.target.value })}
              >
                <option value="sequential">{TEST_TYPE_LABELS.sequential.option}</option>
                <option value="rotational">{TEST_TYPE_LABELS.rotational.option}</option>
              </select>
              {form.test_type === 'rotational' && (
                <p className="text-[13px] text-[var(--wb-text-muted)] mt-1">
                  {TEST_TYPE_LABELS.rotational.description}
                </p>
              )}
              {form.test_type === 'sequential' && (
                <p className="text-[13px] text-[var(--wb-text-muted)] mt-1">
                  {TEST_TYPE_LABELS.sequential.description}
                </p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[var(--wb-text-muted)] mb-1">Слот фото (1–10)</label>
              <input
                type="number"
                min={1}
                max={10}
                className="wb-input w-full"
                value={form.photo_slot}
                onChange={(e) => setForm({ ...form, photo_slot: Number(e.target.value) })}
              />
            </div>
          </div>

          <div ref={cardPickerRef} className="relative">
            <label className="block text-[13px] text-[var(--wb-text-muted)] mb-1">Карточка</label>
            <input
              className="wb-input w-full"
              placeholder="Артикул продавца · Артикул ВБ · Название"
              value={cardSearch}
              onChange={(e) => handleCardInputChange(e.target.value)}
              onFocus={() => {
                setCardDropdownOpen(true);
                if (!cards.length) searchCards(cardSearch.trim());
              }}
              autoComplete="off"
            />
            {form.nm_id && selectedCard && (
              <p className="text-[13px] text-[var(--wb-success)] mt-1">Выбрано: {formatCardLabel(selectedCard)}</p>
            )}
            {cardDropdownOpen && (
              <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto border border-[var(--wb-border)] rounded-lg bg-white shadow-lg divide-y">
                {cardsLoading && (
                  <p className="p-3 text-[13px] text-[var(--wb-text-muted)]">Загрузка карточек…</p>
                )}
                {!cardsLoading && cards.length === 0 && (
                  <p className="p-3 text-[13px] text-[var(--wb-text-muted)]">
                    {cardSearch.trim()
                      ? 'Ничего не найдено по запросу'
                      : cardsTotal === 0
                        ? 'Каталог пуст. Запустите выгрузку карточек: node scripts/wb_cards-list.js'
                        : 'Начните вводить артикул продавца, артикул ВБ или название'}
                  </p>
                )}
                {!cardsLoading && cards.map((c) => (
                  <button
                    key={c.nm_id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleCardSelect(c)}
                    className={`w-full flex items-center gap-3 p-2.5 text-left text-[13px] hover:bg-[var(--wb-surface-muted)] ${
                      String(form.nm_id) === String(c.nm_id) ? 'bg-[var(--wb-primary-soft)]' : ''
                    }`}
                  >
                    {c.main_photo ? (
                      <img src={c.main_photo} alt="" className="w-10 h-10 object-cover rounded shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded shrink-0" />
                    )}
                    <span className="min-w-0">
                      <span className="block font-medium truncate">{c.vendor_code || '—'}</span>
                      <span className="block text-[13px] text-[var(--wb-text-muted)] truncate">
                        {c.nm_id} · {c.title || 'Без названия'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {form.test_type === 'sequential' ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[13px] text-[var(--wb-text-muted)]">Период «до» (с)</label>
                <input type="date" className="wb-input w-full" value={form.baseline_from}
                  onChange={(e) => setForm({ ...form, baseline_from: e.target.value })} />
              </div>
              <div>
                <label className="text-[13px] text-[var(--wb-text-muted)]">Период «до» (по)</label>
                <input type="date" className="wb-input w-full" value={form.baseline_to}
                  onChange={(e) => setForm({ ...form, baseline_to: e.target.value })} />
              </div>
              <div>
                <label className="text-[13px] text-[var(--wb-text-muted)]">Период «после» (с)</label>
                <input type="date" className="wb-input w-full" value={form.test_from}
                  onChange={(e) => setForm({ ...form, test_from: e.target.value })} />
              </div>
              <div>
                <label className="text-[13px] text-[var(--wb-text-muted)]">Период «после» (по)</label>
                <input type="date" className="wb-input w-full" value={form.test_to}
                  onChange={(e) => setForm({ ...form, test_to: e.target.value })} />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-[13px] text-[var(--wb-text-muted)]">Период ротации</label>
              <select
                className="wb-input w-full"
                value={form.rotation_period_days}
                onChange={(e) => setForm({ ...form, rotation_period_days: Number(e.target.value) })}
              >
                <option value={3}>3 дня</option>
                <option value={7}>7 дней</option>
                <option value={14}>14 дней</option>
                <option value={21}>21 день</option>
                <option value={30}>30 дней</option>
              </select>
              <p className="text-[13px] text-[var(--wb-text-muted)] mt-1">
                Изображения меняются каждый час по кругу на протяжении выбранного периода
              </p>
            </div>
          )}

          <div>
            <label className="block text-[13px] text-[var(--wb-text-muted)] mb-1">
              {form.test_type === 'rotational'
                ? 'Изображения для сравнения (2–5 файлов; исходное фото в карточке сохранится автоматически)'
                : 'Новое изображение для теста'}
            </label>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-[var(--wb-border)] rounded-xl p-6 cursor-pointer hover:border-[var(--wb-primary)] transition-colors">
              <HiUpload className="w-8 h-8 text-[var(--wb-text-muted)] mb-2" />
              <span className="text-[13px] text-[var(--wb-text-secondary)]">
                {uploadFiles.length
                  ? `${uploadFiles.length} файл(ов) выбрано`
                  : 'Нажмите или перетащите изображения'}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple={form.test_type === 'rotational'}
                className="hidden"
                onChange={(e) => setUploadFiles([...(e.target.files || [])])}
              />
            </label>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="wb-btn-secondary"
              disabled={creating}
              onClick={() => setShowCreate(false)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="wb-btn"
              disabled={creating}
              onClick={handleCreate}
            >
              {creating
                ? (form.test_type === 'rotational' ? 'Сохранение…' : 'Создание…')
                : `Создать ${form.test_type === 'rotational' ? 'и запустить' : ''}`}
            </button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-4 flex-1 min-h-0 items-stretch">
        <div className="lg:col-span-2 min-w-0 flex">
          <div className="wb-card flex flex-col w-full h-full min-h-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--wb-border)] shrink-0 bg-[var(--wb-surface-muted)]/40 flex items-center justify-between gap-2">
              <h3 className="font-semibold text-[15px]">Тесты</h3>
              <button
                type="button"
                onClick={() => setShowCreate(!showCreate)}
                className="wb-btn text-[13px] flex items-center gap-1 px-2.5 py-1.5 shrink-0"
              >
                <HiPlus className="w-3.5 h-3.5" /> Новый тест
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 min-h-0 bg-[var(--wb-surface-muted)]/20">
              {loading && <p className="p-2 text-[13px] text-[var(--wb-text-muted)]">Загрузка…</p>}
              {!loading && tests.length === 0 && (
                <p className="p-2 text-[13px] text-[var(--wb-text-muted)]">Нет тестов. Создайте первый.</p>
              )}
              <div className="space-y-2">
              {tests.map((t) => (
                <div
                  key={t.id}
                  className={`w-full p-3 text-[13px] rounded-lg border transition-all shadow-sm ${
                    selectedTestId === t.id
                      ? 'bg-white border-[var(--wb-primary)] ring-2 ring-[var(--wb-primary)]/30'
                      : 'bg-white border-[var(--wb-border)] hover:border-[var(--wb-primary)]/60 hover:shadow'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedTestId(t.id)}
                    className="w-full text-left"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-medium truncate">{t.vendor_code || `nm ${t.nm_id}`}</span>
                      <span className={`text-[13px] px-2 py-0.5 rounded-full shrink-0 font-medium ${statusBadgeClass(t.status)}`}>
                        {STATUS_LABELS[t.status] || t.status}
                      </span>
                    </div>
                    <p className="text-[13px] text-[var(--wb-text-muted)] mt-1">
                      {testTypeLabel(t.test_type)} · слот {t.photo_slot || 1}
                    </p>
                  </button>
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      className="text-[13px] text-[var(--wb-danger)] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                      disabled={deletingId === t.id}
                      onClick={(e) => handleDelete(t.id, e)}
                      title="Удалить тест"
                    >
                      <HiTrash className="w-3.5 h-3.5" />
                      {deletingId === t.id ? 'Удаление…' : 'Удалить'}
                    </button>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-10 min-w-0">
          <div className="space-y-4 min-w-0">
          {!selectedTestId && (
            <div className="wb-card p-8 text-center text-[var(--wb-text-muted)]">
              <HiChartBar className="w-12 h-12 mx-auto mb-2 opacity-40" />
              Выберите тест для просмотра результатов
            </div>
          )}

          {selectedTestId && detailLoading && (
            <p className="text-[13px] text-[var(--wb-text-muted)]">Загрузка результатов…</p>
          )}

          {test && results && !detailLoading && (
            <>
              <div className="wb-card p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-[15px]">{test.title || test.vendor_code || `nm ${test.nm_id}`}</h3>
                    <span className={`text-[13px] px-2 py-0.5 rounded-full font-medium ${statusBadgeClass(test.status)}`}>
                      {STATUS_LABELS[test.status] || test.status}
                    </span>
                  </div>
                  <p className="text-[13px] text-[var(--wb-text-muted)]">
                    {test.test_type === 'rotational'
                      ? `Период ${test.rotation_period_days || 7} дн. · смена каждый час · ${test.variant_count || 0} изображений`
                      : `До: ${test.baseline_from} — ${test.baseline_to} · После: ${test.test_from} — ${test.test_to}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  {test.status === 'draft' && (
                    <button
                      type="button"
                      className="wb-btn text-[13px] flex items-center gap-1"
                      disabled={startingId === test.id}
                      onClick={() => handleStart(test.id)}
                    >
                      <HiPlay className="w-4 h-4" />
                      {startingId === test.id ? 'Запуск…' : 'Запустить'}
                    </button>
                  )}
                  {test.status === 'running' && (
                    <button
                      type="button"
                      className="wb-btn-secondary text-[13px] flex items-center gap-1"
                      disabled={completingId === test.id}
                      onClick={() => handleComplete(test.id)}
                    >
                      <HiCheck className="w-4 h-4" />
                      {completingId === test.id ? 'Завершение…' : 'Завершить'}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <TrafficLayerToggle layer={metricLayer} onChange={setMetricLayer} hasAd={hasAd} />
                <select
                  className="wb-input text-[13px]"
                  value={chartMetric}
                  onChange={(e) => setChartMetric(e.target.value)}
                >
                  {chartMetricOptions.map((m) => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
              </div>

              {results.test_type === 'rotational' ? (
                <div className="space-y-4">
                  <AbComparisonChart
                    chart={results.chart}
                    seriesData={chartSeries}
                    metricKey={chartMetric}
                    metricLabel={metricLabel}
                    layer={metricLayer}
                    pivotLabel="Смена фото"
                    testType={results.test_type}
                  />

                  <div className="grid lg:grid-cols-12 gap-4 items-stretch">
                    {results.variants?.length > 0 && (
                      <div className="lg:col-span-5 wb-card p-4 min-w-0 h-full flex flex-col">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 shrink-0">
                          <h4 className="font-semibold text-[15px]">Сводка по вариантам</h4>
                          {test?.status === 'running' && (
                            <span className="text-[13px] text-[var(--wb-text-muted)]">
                              Победитель появится после завершения теста
                            </span>
                          )}
                        </div>
                        <div className="space-y-4 flex-1 min-h-0">
                          {results.original_baseline && (
                            <VariantSummaryCard
                              row={results.original_baseline}
                              testId={test?.id || selectedTestId}
                              clientId={clientId}
                              metricLayer={metricLayer}
                              hasAd={hasAd || results.original_baseline.has_ad_data}
                              isOriginal
                              isWinner={test?.status === 'completed' && results.original_baseline.is_winner}
                              canApply={test?.status === 'completed'}
                              applying={applyingVariantId === 'original'}
                              onApply={() => handleApplyVariant(
                                'original',
                                results.original_baseline.label || 'Исходный креатив',
                                { isOriginal: true },
                              )}
                            />
                          )}
                          {results.variants.map((v) => (
                            <VariantSummaryCard
                              key={v.variant_id}
                              row={v}
                              testId={test?.id || selectedTestId}
                              clientId={clientId}
                              metricLayer={metricLayer}
                              hasAd={hasAd}
                              isWinner={test?.status === 'completed' && v.is_winner}
                              canApply={test?.status === 'completed'}
                              applying={applyingVariantId === v.variant_id}
                              onApply={() => handleApplyVariant(v.variant_id, v.label)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={`min-w-0 h-full ${results.variants?.length ? 'lg:col-span-7' : 'lg:col-span-12'}`}>
                      <AbStatsTable
                        statsTable={results.stats_table}
                        metricLayer={metricLayer}
                        granularity={tableGranularity}
                        onGranularityChange={setTableGranularity}
                        hasAd={hasAd}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <AbComparisonChart
                    chart={results.chart}
                    seriesData={chartSeries}
                    metricKey={chartMetric}
                    metricLabel={metricLabel}
                    layer={metricLayer}
                    pivotLabel="Смена фото"
                    testType={results.test_type}
                  />

                  {results.comparison && (
                    <div className="wb-card overflow-hidden">
                      <table className="w-full text-[13px]">
                        <thead className="bg-[var(--wb-surface-muted)]">
                          <tr>
                            <th className="text-left p-3">Метрика</th>
                            <th className="text-right p-3">До</th>
                            <th className="text-right p-3">После</th>
                            <th className="text-right p-3">Δ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {METRIC_OPTIONS.map((metric) => {
                            const row = results.comparison?.[metric.key];
                            if (!row) return null;
                            if (!isAdMetricVisibleInLayer(metric.key, metricLayer)) return null;
                            const delta = layerDeltaPct(row, metricLayer, metric.key);
                            return (
                            <tr key={metric.key} className="border-t border-[var(--wb-border)]">
                              <td className="p-3">
                                {metric.label}
                                {(metric.adOnly || metric.showInAll) && (
                                  <span className="ml-1 text-[13px] text-[var(--wb-text-muted)]">(реклама)</span>
                                )}
                              </td>
                              <td className="p-3 text-right tabular-nums">
                                {formatMetric(layerValue(row.baseline, metricLayer, metric.key), metric.key)}
                              </td>
                              <td className="p-3 text-right tabular-nums">
                                {formatMetric(layerValue(row.test, metricLayer, metric.key), metric.key)}
                              </td>
                              <td className={`p-3 text-right tabular-nums font-medium ${
                                delta != null && delta > 0 ? 'text-[var(--wb-success)]' : delta != null && delta < 0 ? 'text-[var(--wb-danger)]' : ''
                              }`}>
                                {delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta}%`}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {hasAd && (
                    <p className="text-[13px] text-[var(--wb-text-muted)] wb-card p-3">
                      Данные собираются по кабинету, рекламе и органике. Переключите слой, чтобы сравнить
                      результаты по выбранному источнику трафика.
                      {results.chart?.granularity === 'hour' && ' Почасовая воронка доступна для активных тестов.'}
                    </p>
                  )}
                </>
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
